import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || window.location.origin || '').replace(/\/$/, '');
const TURNSTILE_SITE_KEY = process.env.REACT_APP_JCAL_TURNSTILE_SITE_KEY || process.env.REACT_APP_TURNSTILE_SITE_KEY;

const CATEGORIES = ['Bug report', 'Missing event', 'Wrong event info', 'Feature request', 'Other'];

export default function JCalFeedback() {
    const [category, setCategory] = useState('');
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [honeypot, setHoneypot] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [startTime] = useState(() => Date.now());
    const widgetRef = useRef(null);
    const widgetIdRef = useRef(null);

    useLayoutEffect(() => {
        const prev = document.body.style.background;
        const prevHtml = document.documentElement.style.background;
        const root = document.getElementById('root');
        const prevRoot = root?.style.background;
        document.documentElement.style.setProperty('background', '#ffffff', 'important');
        document.body.style.setProperty('background', '#ffffff', 'important');
        if (root) root.style.setProperty('background', '#ffffff', 'important');
        return () => {
            document.documentElement.style.background = prevHtml;
            document.body.style.background = prev;
            if (root) root.style.background = prevRoot || '';
        };
    }, []);

    useEffect(() => {
        if (!TURNSTILE_SITE_KEY || typeof window === 'undefined') return;
        let cancelled = false;
        const render = () => {
            if (cancelled || !widgetRef.current || widgetIdRef.current) return;
            if (window.turnstile?.render) {
                widgetIdRef.current = window.turnstile.render(widgetRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    theme: 'light',
                    action: 'jcal_web_feedback',
                    callback: (t) => setTurnstileToken(t),
                    'error-callback': () => setError('Verification failed. Please refresh and try again.'),
                    'expired-callback': () => setTurnstileToken(''),
                });
                return;
            }
            setTimeout(render, 120);
        };
        render();
        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile?.remove) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim() || honeypot) return;
        if (Date.now() - startTime < 1200) { setError('Please slow down and try again.'); return; }
        if (TURNSTILE_SITE_KEY && !turnstileToken) { setError('Please complete the verification below.'); return; }
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API_BASE_URL}/api/jcal/web-feedback`, {
                message: message.trim(),
                category: category || null,
                email: email.trim() || null,
                turnstile_token: turnstileToken || 'missing',
            });
            setSubmitted(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(detail || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="jcalfb-page">
            <header className="jcalfb-header">
                <a href="/" className="jcalfb-logo-link">
                    <img src="/jcal-logo-transparent.png" alt="JCAL" className="jcalfb-logo" />
                </a>
            </header>

            <main className="jcalfb-main">
                {submitted ? (
                    <div className="jcalfb-success">
                        <div className="jcalfb-success-icon">✓</div>
                        <h2>Thanks for your feedback!</h2>
                        <p>We read every message and use it to improve JCAL.</p>
                        <a href="/" className="jcalfb-back-link">Back to JCAL.app</a>
                    </div>
                ) : (
                    <>
                        <h1 className="jcalfb-title">Send Feedback</h1>
                        <p className="jcalfb-sub">Bug, missing event, feature idea — all feedback welcome.</p>

                        <form onSubmit={handleSubmit} className="jcalfb-form">
                            <div className="honeypot">
                                <label>
                                    <span>Leave empty</span>
                                    <input type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex="-1" autoComplete="off" />
                                </label>
                            </div>

                            <div className="jcalfb-field">
                                <label className="jcalfb-label" htmlFor="jcalfb-category">Category <span className="jcalfb-optional">(optional)</span></label>
                                <select id="jcalfb-category" className="jcalfb-select" value={category} onChange={e => setCategory(e.target.value)}>
                                    <option value="">— Select —</option>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>

                            <div className="jcalfb-field">
                                <label className="jcalfb-label" htmlFor="jcalfb-message">Your message</label>
                                <textarea
                                    id="jcalfb-message"
                                    className="jcalfb-textarea"
                                    rows={6}
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    placeholder="Tell us what's on your mind…"
                                    required
                                />
                            </div>

                            <div className="jcalfb-field">
                                <label className="jcalfb-label" htmlFor="jcalfb-email">Your email <span className="jcalfb-optional">(optional — only if you want a reply)</span></label>
                                <input
                                    id="jcalfb-email"
                                    type="email"
                                    className="jcalfb-input"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                />
                            </div>

                            {TURNSTILE_SITE_KEY && (
                                <div className="jcalfb-turnstile">
                                    <div ref={widgetRef} />
                                </div>
                            )}

                            {error && <p className="jcalfb-error">{error}</p>}

                            <button type="submit" className="jcalfb-submit" disabled={loading || !message.trim()}>
                                {loading ? 'Sending…' : 'Send Feedback'}
                            </button>
                        </form>
                    </>
                )}
            </main>
        </div>
    );
}
